<script setup lang="ts">
import { defineComponent, h, ref } from 'vue'
import { colors, PopupBasicHeader } from 'wepin-vue-component'
import AccountManage from './AccountManage.vue'
import { AddressCopyViewer, WrappedNetworkIcon, PopupList } from '@/components'
import DoneCopyModal from '@/components/popup/DoneCopyModal.vue'
import type { TypePopupItem } from '@/components/popup/list/PopupList.vue'
import {
  useAccountQuery,
  useBreakPointsClass,
  useI18nTypes,
} from '@/composables'
import { defaultBreakPoints } from '@/const'
const emits = defineEmits<{
  (event: 'close'): void
  (event: 'select-route', name: string): void
}>()
const { t } = useI18nTypes()
const { breakPointsClass } = useBreakPointsClass(defaultBreakPoints)
const { account } = useAccountQuery()

const onClickChangeLabel = () => {
  emits('select-route', 'ChangeLabel')
}
const onClickHide = () => {
  emits('select-route', 'HideAccount')
}
const listItems = ref<TypePopupItem[]>([
  {
    name: t('account_manage.change_label'),
    type: 'item',
    button: {
      type: 'element',
      name: 'Right',
      size: 'medium',
      color: colors.currentThemeRef.value.grey_111,
    },
    onClick: onClickChangeLabel,
  },
  {
    name: t('account_manage.delete'),
    type: 'item',
    button: {
      type: 'element',
      name: 'Right',
      size: 'medium',
      color: colors.currentThemeRef.value.grey_111,
    },
    onClick: onClickHide,
  },
])
const doneCopyModal = ref(false)
const onClickCopy = () => {
  if (doneCopyModal.value) return
  doneCopyModal.value = true
  setTimeout(() => {
    doneCopyModal.value = false
  }, 2000)
}
</script>
<script lang="ts">
export const createAccountManageRoute = (params: {
  onSelectRoute: (name: string) => void
}) => {
  return defineComponent<typeof AccountManage>((props) => {
    return () =>
      h(AccountManage, {
        'onSelect-route': (name: string) => {
          params.onSelectRoute(name)
        },
        ...props,
      })
  })
}
</script>

<template>
  <div v-if="account" class="account-manage" :class="[breakPointsClass]">
    <PopupBasicHeader class="header" title="" :exit="() => emits('close')" />
    <div class="manage-content">
      <WrappedNetworkIcon
        class="network-icon"
        :token-src="account.coin?.iconUrl"
        :network-src="account.coin?.coin?.iconUrl"
        size="custom"
        custom-size="58px"
      />
      <div class="coin-name">{{ account.coin?.name }}</div>
      <div class="account-label">{{ account.label }}</div>
      <AddressCopyViewer
        :address="account.address"
        :modal="doneCopyModal"
        @open-modal="onClickCopy"
      />
      <PopupList class="popup-list" :items="listItems" />
      <div class="button-area">
        <div class="popup-button"></div>
        <div class="popup-button"></div>
      </div>
    </div>
    <DoneCopyModal :open="doneCopyModal" />
  </div>
</template>

<style lang="scss" scoped>
@import 'wepin-vue-component/src/styles/typeface.module.scss';
.account-manage {
  text-align: center;
  width: 460px;
  background-color: var(--grey_fff);
  border-radius: 8px;
  box-shadow: 0px 20px 40px rgba(0, 0, 0, 0.2);

  &:deep(.header) {
    padding: 20px !important;
  }
  .manage-content {
    padding: 0 30px 30px 30px;
    & > .network-icon {
      display: inline-flex !important;
    }

    & > .coin-name {
      @include typeface('Title_24');
      color: var(--grey_111);
      margin-top: 20px;
    }
    & > .account-label {
      @include typeface('Paragraph_16_r');
      color: var(--grey_999);
    }
    & > .popup-list {
      text-align: left;
      margin: 0;
    }
  }
}
.account-manage.mobile {
  width: 100%;
  padding: 20px;
  padding-top: 0;
  .manage-content {
    padding: 0;
  }
  .popup-list-item {
    margin: 0;
  }
}
</style>
